import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

@customElement('sulfadimethoxine-calculator')
export class SulfadimethoxineCalculator extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state() private weight = 0;
  @state() private weightUnit = 'kg';
  @state() private purpose = 'initial';
  @state() private formulation = 'liquid';
  @state() private liquidConcMode = 'preset'; // 'preset' | 'custom'
  @state() private liquidConcPreset = 5; // % default (5% = 50 mg/mL)
  @state() private liquidConcMgPerMl = 50; // used when custom
  @state() private tabletStrengthMg = 500;
  @state() private resultText = '';
  @state() private details = '';

  private concPresets = [2.5, 5, 10, 12.5]; // Common % concentrations for sulfadimethoxine

  private toKg(val: number, unit: string) {
    return unit === 'lb' ? val / 2.20462 : val;
  }

  private dosageRateMgPerKg() {
    switch (this.purpose) {
      case 'initial':
        return 50; // 50 mg/kg loading dose (day 1)
      case 'maintenance':
        return 25; // 25 mg/kg maintenance dose (day 2+)
      default:
        return 50;
    }
  }

  private round(n: number, places = 2) {
    const f = Math.pow(10, places);
    return Math.round(n * f) / f;
  }

  private effectiveLiquidConc() {
    if (this.liquidConcMode === 'preset') {
      // Convert % to mg/mL: % / 100 * 1000
      return (this.liquidConcPreset / 100) * 1000;
    }
    return this.liquidConcMgPerMl;
  }

  private calculate() {
    if (!this.weight || this.weight <= 0) {
      this.resultText = '';
      this.details = '';
      return;
    }

    const kg = this.toKg(this.weight, this.weightUnit);
    const mgNeeded = kg * this.dosageRateMgPerKg();

    if (this.formulation === 'liquid') {
      const conc = this.effectiveLiquidConc();
      if (!conc || conc <= 0) {
        this.resultText = '';
        this.details = '';
        return;
      }
      const mL = mgNeeded / conc;
      this.resultText = `${this.round(mL, 2)} mL`;
      this.details = `${this.round(mgNeeded, 1)} mg total @ ${conc} mg/mL`;
    } else {
      if (!this.tabletStrengthMg || this.tabletStrengthMg <= 0) {
        this.resultText = '';
        this.details = '';
        return;
      }
      const exactTablets = mgNeeded / this.tabletStrengthMg;
      const wholeTablets = Math.ceil(exactTablets * 2) / 2; // Round to nearest half tablet
      this.resultText = `${this.round(exactTablets, 2)} tablets (≈ ${this.round(wholeTablets, 1)} if rounding to ½ tablet)`;
      this.details = `${this.round(mgNeeded, 1)} mg total @ ${this.tabletStrengthMg} mg per tablet`;
    }
    this.requestUpdate()
  }

  private handleWeightChange(e: Event) {
    const target = e.target as HTMLInputElement;
    this.weight = parseFloat(target.value) || 0;
    this.calculate();
  }

  private handleWeightUnitChange(e: Event) {
    const target = e.target as HTMLSelectElement;
    this.weightUnit = target.value;
    this.calculate();
  }

  private handlePurposeChange(e: Event) {
    const target = e.target as HTMLInputElement;
    this.purpose = target.value;
    this.calculate();
  }

  private handleFormulationChange(e: Event) {
    const target = e.target as HTMLInputElement;
    this.formulation = target.value;
    this.calculate();
  }

  private handleLiquidConcChange(e: Event) {
    const target = e.target as HTMLInputElement;
    this.liquidConcMgPerMl = parseFloat(target.value) || 0;
    this.calculate();
  }

  private handleTabletStrengthChange(e: Event) {
    const target = e.target as HTMLInputElement;
    this.tabletStrengthMg = parseFloat(target.value) || 0;
    this.calculate();
  }

  private handleConcPresetChange(preset: number) {
    this.liquidConcMode = 'preset';
    this.liquidConcPreset = preset;
    this.calculate();
  }

  private handleConcModeChange(mode: 'preset' | 'custom') {
    this.liquidConcMode = mode;
    this.calculate();
  }

  render() {
    return html`
      <div class="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
        <h2 class="text-2xl font-bold text-gray-900 mb-6">Sulfadimethoxine Dosage Calculator</h2>

        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Dog Weight</label>
          <div class="flex gap-2">
            <input
              type="number"
              class="block px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex-1"
              placeholder="Enter weight"
              .value=${this.weight.toString()}
              @input=${this.handleWeightChange}
              min="0"
              step="0.1"
            />
            <select class="block w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" .value=${this.weightUnit} @change=${this.handleWeightUnitChange}>
              <option value="kg">kg</option>
              <option value="lb">lb</option>
            </select>
          </div>
        </div>

        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Treatment Purpose</label>
          <div class="flex flex-wrap gap-4 mt-2">
            <div class="flex items-center">
              <input
                type="radio"
                id="initial"
                name="purpose"
                value="initial"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                .checked=${this.purpose === 'initial'}
                @change=${this.handlePurposeChange}
              />
              <label for="initial" class="ml-2 text-sm text-gray-700">Loading Dose - Day 1 (50 mg/kg)</label>
            </div>
            <div class="flex items-center">
              <input
                type="radio"
                id="maintenance"
                name="purpose"
                value="maintenance"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                .checked=${this.purpose === 'maintenance'}
                @change=${this.handlePurposeChange}
              />
              <label for="maintenance" class="ml-2 text-sm text-gray-700">Maintenance - Day 2+ (25 mg/kg)</label>
            </div>
          </div>
        </div>

        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Formulation</label>
          <div class="flex flex-wrap gap-4 mt-2">
            <div class="flex items-center">
              <input
                type="radio"
                id="liquid"
                name="formulation"
                value="liquid"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                .checked=${this.formulation === 'liquid'}
                @change=${this.handleFormulationChange}
              />
              <label for="liquid" class="ml-2 text-sm text-gray-700">Liquid/Suspension</label>
            </div>
            <div class="flex items-center">
              <input
                type="radio"
                id="tablet"
                name="formulation"
                value="tablet"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                .checked=${this.formulation === 'tablet'}
                @change=${this.handleFormulationChange}
              />
              <label for="tablet" class="ml-2 text-sm text-gray-700">Tablet</label>
            </div>
          </div>
        </div>

        ${this.formulation === 'liquid' ? html`
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Liquid Concentration</label>

            <!-- Preset buttons -->
            <div class="grid grid-cols-5 gap-2 mb-3">
              ${this.concPresets.map(preset => html`
                <button type="button"
                  @click=${() => this.handleConcPresetChange(preset)}
                  class="py-2 rounded-xl border-2 transition-all text-sm ${
                    this.liquidConcMode === 'preset' && this.liquidConcPreset === preset
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'text-gray-500 hover:border-blue-200 border-gray-300'
                  }">
                  ${preset}%
                </button>
              `)}

              <!-- Custom toggle -->
              <button type="button"
                @click=${() => this.handleConcModeChange('custom')}
                class="py-2 rounded-xl border-2 transition-all text-sm ${
                  this.liquidConcMode === 'custom'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'text-gray-500 hover:border-blue-200 border-gray-300'
                }">
                Custom
              </button>
            </div>

            <!-- Custom input -->
            ${this.liquidConcMode === 'custom' ? html`
              <div class="flex gap-3 items-center">
                <input
                  type="number"
                  class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  .value=${this.liquidConcMgPerMl.toString()}
                  @input=${this.handleLiquidConcChange}
                  min="0"
                  step="0.1"
                  placeholder="Enter mg/mL (e.g., 50)"
                />
                <span class="text-sm text-gray-500">mg/mL</span>
              </div>
            ` : ''}
          </div>
        ` : html`
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Tablet Strength (mg)</label>
            <input
              type="number"
              class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              .value=${this.tabletStrengthMg.toString()}
              @input=${this.handleTabletStrengthChange}
              min="0"
              step="1"
              placeholder="Enter tablet strength"
            />
          </div>
        `}

        ${this.resultText ? html`
          <div class="mt-6 p-4 bg-blue-50 rounded-lg">
            <div class="text-lg font-semibold text-blue-900">Dosage: ${this.resultText}</div>
            ${this.details ? html`<div class="text-sm text-blue-700 mt-2">${this.details}</div>` : ''}
          </div>
        ` : ''}

        <div class="text-xs text-gray-600 mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
          <strong>Important:</strong> This calculator is for veterinary professional use only.
          <span>
            Always consult with a veterinarian before administering any medication.
            Sulfadimethoxine is typically given once daily for coccidiosis treatment.
            Initial dose is usually followed by maintenance doses for 5-7 days.
          </span>
        </div>
      </div>
    `;
  }
}
